import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

function fmtDateTime(d) {
  if (!d) return '-';
  return new Date(d).toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const REASON_COLORS = {
  'Barang masuk':            { bg: '#E8F5E9', c: '#1B5E20' },
  'Restok':                  { bg: '#E8F5E9', c: '#1B5E20' },
  'Inisialisasi stok':       { bg: '#E3F2FD', c: '#0D47A1' },
  'Set manual':              { bg: '#E3F2FD', c: '#0D47A1' },
  'Barang rusak/kadaluarsa': { bg: '#FFEBEE', c: '#C62828' },
  'Barang rusak':            { bg: '#FFEBEE', c: '#C62828' },
};

function reasonColor(reason) {
  for (const [key, val] of Object.entries(REASON_COLORS)) {
    if (reason?.includes(key)) return val;
  }
  if (reason?.startsWith('Pesanan') || reason?.startsWith('Paket')) return { bg: '#FFF3E0', c: '#E65100' };
  if (reason?.startsWith('Batal')) return { bg: '#F3E5F5', c: '#4A148C' };
  return { bg: '#F5F5F5', c: '#555' };
}

// Slot = satu pilihan di dropdown. Bisa mewakili item (tanpa varian) atau satu varian spesifik.
// { key, label, groupLabel, menu_item_id, menu_variant_id | null }

export default function KartuStok() {
  const [slots, setSlots]         = useState([]);   // daftar pilihan dropdown
  const [selected, setSelected]   = useState('');   // slot.key yang dipilih
  const [logs, setLogs]           = useState([]);
  const [saldo, setSaldo]         = useState(0);
  const [saldoUnit, setSaldoUnit] = useState('porsi');
  const [loading, setLoading]     = useState(false);
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState('');

  useEffect(() => { loadSlots(); }, []);

  async function loadSlots() {
    const { data } = await supabase
      .from('menu_items')
      .select('id, name, emoji, category, code, menu_variants(id, name, sort_order)')
      .order('category')
      .order('name');

    const list = [];
    (data || []).forEach(item => {
      const variants = (item.menu_variants || []).sort((a, b) => a.sort_order - b.sort_order);
      if (variants.length === 0) {
        list.push({
          key:             item.id,
          label:           `${item.emoji} ${item.name}${item.code ? ' (' + item.code + ')' : ''}`,
          groupLabel:      null,
          menu_item_id:    item.id,
          menu_variant_id: null,
        });
      } else {
        variants.forEach(v => {
          list.push({
            key:             `${item.id}__${v.id}`,
            label:           `${item.emoji} ${item.name} — ${v.name}`,
            groupLabel:      `${item.emoji} ${item.name}`,
            menu_item_id:    item.id,
            menu_variant_id: v.id,
          });
        });
      }
    });
    setSlots(list);
  }

  async function loadLogs(slotKey) {
    if (!slotKey) return;
    const slot = slots.find(s => s.key === slotKey);
    if (!slot) return;
    setLoading(true);

    // Query stock_log dengan filter menu_item_id + menu_variant_id
    let q = supabase
      .from('stock_log')
      .select('id, delta, reason, reference_id, created_at, menu_variant_id')
      .eq('menu_item_id', slot.menu_item_id)
      .order('created_at', { ascending: false })
      .limit(200);

    if (slot.menu_variant_id) {
      q = q.eq('menu_variant_id', slot.menu_variant_id);
    } else {
      q = q.is('menu_variant_id', null);
    }

    if (dateFrom) q = q.gte('created_at', dateFrom);
    if (dateTo)   q = q.lte('created_at', dateTo + 'T23:59:59');

    const { data: logData } = await q;
    setLogs(logData || []);

    // Ambil saldo sekarang dari tabel stock — cocokkan menu_variant_id
    let sq = supabase
      .from('stock')
      .select('qty, unit')
      .eq('menu_item_id', slot.menu_item_id);

    if (slot.menu_variant_id) {
      sq = sq.eq('menu_variant_id', slot.menu_variant_id);
    } else {
      sq = sq.is('menu_variant_id', null);
    }

    const { data: stockData } = await sq.maybeSingle();
    setSaldo(stockData?.qty ?? 0);
    setSaldoUnit(stockData?.unit || 'porsi');
    setLoading(false);
  }

  function handleSelect(key) {
    setSelected(key);
    setLogs([]);
    setSaldo(0);
    if (key) loadLogs(key);
  }

  function handleFilter() { if (selected) loadLogs(selected); }

  const logsWithSaldo = (() => {
    let s = saldo;
    return logs.map(l => {
      const saldoSetelah = s;
      s = s - l.delta;
      return { ...l, saldo_setelah: saldoSetelah };
    });
  })();

  const selectedSlot = slots.find(s => s.key === selected);
  const totalMasuk   = logs.filter(l => l.delta > 0).reduce((s, l) => s + l.delta, 0);
  const totalKeluar  = logs.filter(l => l.delta < 0).reduce((s, l) => s + Math.abs(l.delta), 0);

  // Kelompokkan slot untuk tampilan dropdown yang lebih rapi
  // Slot dengan groupLabel sama ditampilkan berurutan (sudah terurut dari query)
  const renderedOptions = [];
  let lastGroup = null;
  slots.forEach(s => {
    if (s.groupLabel && s.groupLabel !== lastGroup) {
      renderedOptions.push({ type: 'optgroup', label: s.groupLabel });
      lastGroup = s.groupLabel;
    } else if (!s.groupLabel) {
      lastGroup = null;
    }
    renderedOptions.push({ type: 'option', slot: s });
  });

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem' }}>Kartu Stok</div>
        <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>Riwayat mutasi stok per item / varian</div>
      </div>

      {/* Filter */}
      <div style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '16px 18px', marginBottom: 16, boxShadow: 'var(--shadow)', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>

        {/* Dropdown item / varian */}
        <div style={{ flex: '1 1 240px' }}>
          <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>Item / Varian</label>
          <select value={selected} onChange={e => handleSelect(e.target.value)}
            style={{ width: '100%', border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 12px', fontSize: '.86rem', outline: 'none', cursor: 'pointer' }}>
            <option value="">Pilih item / varian...</option>
            {renderedOptions.map((r, idx) => {
              if (r.type === 'optgroup') {
                return (
                  <option key={'grp-' + idx} disabled
                    style={{ fontWeight: 700, color: '#555', background: '#F5F5F5' }}>
                    ── {r.label}
                  </option>
                );
              }
              return (
                <option key={r.slot.key} value={r.slot.key}
                  style={{ paddingLeft: r.slot.groupLabel ? 16 : 0 }}>
                  {r.slot.groupLabel ? '  ' : ''}{r.slot.label}
                </option>
              );
            })}
          </select>
        </div>

        {/* Rentang tanggal */}
        <div>
          <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>Dari</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 10px', fontSize: '.82rem', outline: 'none' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>Sampai</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ border: '1.5px solid #DDD', borderRadius: 8, padding: '8px 10px', fontSize: '.82rem', outline: 'none' }} />
        </div>
        <button
          onClick={handleFilter}
          disabled={!selected}
          style={{
            padding: '8px 18px', borderRadius: 8,
            background: selected ? '#CD5226' : '#E0E0E0',
            color: selected ? '#fff' : '#999',
            border: 'none', fontSize: '.82rem',
            cursor: selected ? 'pointer' : 'not-allowed',
            fontWeight: 500, transition: 'background .15s',
          }}>
          Tampilkan
        </button>
      </div>

      {/* Badge item/varian yang dipilih */}
      {selectedSlot && (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>Menampilkan:</span>
          <span style={{ background: selectedSlot.menu_variant_id ? '#EDE7F6' : 'var(--cream)', color: selectedSlot.menu_variant_id ? '#4527A0' : 'var(--dark)', borderRadius: 20, padding: '3px 12px', fontSize: '.78rem', border: '1px solid var(--border)', fontWeight: 500 }}>
            {selectedSlot.label}
          </span>
        </div>
      )}

      {/* Ringkasan statistik */}
      {selectedSlot && logs.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 16 }}>
          {[
            ['Saldo Sekarang', saldo + ' ' + saldoUnit,              'var(--dark)'],
            ['Total Masuk',    '+' + totalMasuk + ' ' + saldoUnit,   '#1B5E20'],
            ['Total Keluar',   '-' + totalKeluar + ' ' + saldoUnit,  '#C62828'],
            ['Jumlah Mutasi',  logs.length + ' transaksi',           'var(--muted)'],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '12px 14px', boxShadow: 'var(--shadow)' }}>
              <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.1rem', color: c }}>{v}</div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '.6rem', letterSpacing: '.8px', textTransform: 'uppercase', color: 'var(--muted)', marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabel log */}
      {selected && (
        loading
          ? <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>Memuat...</div>
          : logs.length === 0
            ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted)', fontSize: '.84rem', background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }}>
                Belum ada mutasi stok untuk {selectedSlot?.label || 'item ini'}
              </div>
            )
            : (
              <div style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
                  <thead>
                    <tr>
                      {['Waktu', 'Keterangan', 'Referensi', 'Mutasi', 'Saldo'].map(h => (
                        <th key={h} style={{ background: '#CD5226', color: '#fff', padding: '9px 14px', textAlign: 'left', fontSize: '.64rem', letterSpacing: '.8px', textTransform: 'uppercase', fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logsWithSaldo.map(l => {
                      const rc = reasonColor(l.reason);
                      return (
                        <tr key={l.id}
                          onMouseEnter={e => e.currentTarget.style.background = '#FDFAF6'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          style={{ transition: 'background .1s' }}>
                          <td style={{ padding: '8px 14px', borderBottom: '1px solid #F5EEE6', fontSize: '.76rem', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{fmtDateTime(l.created_at)}</td>
                          <td style={{ padding: '8px 14px', borderBottom: '1px solid #F5EEE6' }}>
                            <span style={{ background: rc.bg, color: rc.c, borderRadius: 20, padding: '2px 8px', fontSize: '.7rem', fontFamily: "'DM Mono',monospace" }}>{l.reason}</span>
                          </td>
                          <td style={{ padding: '8px 14px', borderBottom: '1px solid #F5EEE6', fontSize: '.72rem', color: 'var(--muted)', fontFamily: "'DM Mono',monospace" }}>
                            {l.reference_id ? l.reference_id.slice(0, 10) + '...' : '—'}
                          </td>
                          <td style={{ padding: '8px 14px', borderBottom: '1px solid #F5EEE6', fontFamily: "'DM Mono',monospace", fontWeight: 500, color: l.delta > 0 ? '#1B5E20' : '#C62828', whiteSpace: 'nowrap' }}>
                            {l.delta > 0 ? '+' : ''}{l.delta}
                          </td>
                          <td style={{ padding: '8px 14px', borderBottom: '1px solid #F5EEE6', fontFamily: "'DM Mono',monospace", fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {l.saldo_setelah}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
      )}
    </div>
  );
}
